/**
 * Public exam archive AJAX runtime.
 */

import { lpSetLoadingEl } from '../../utils.js';

export class ExamArchive {
	static selectors = {
		archive: '.lp-exam-archive',
		results: '.lp-exam-archive__results',
		target: '.lp-exam-archive__results .lp-target',
		sidebar: '.lp-exam-archive__sidebar',
		searchForm: '.lp-exam-archive__search',
		searchInput: '.lp-exam-archive__search input[name="exam_search"]',
		sort: '.lp-exam-archive__sort',
		category: 'input[name="exam_category[]"]',
		pagination: '.learn-press-pagination a.page-numbers',
		clear: '.lp-exam-archive__clear',
	};

	constructor() {
		this.archive = document.querySelector( ExamArchive.selectors.archive );
		if ( ! this.archive || ! window.lpAJAXG ) {
			return;
		}

		this.results = this.archive.querySelector( ExamArchive.selectors.results );
		this.target = this.archive.querySelector( ExamArchive.selectors.target );
		this.sidebar = this.archive.querySelector( ExamArchive.selectors.sidebar );
		this.requestId = 0;

		if ( ! this.results || ! this.target ) {
			return;
		}

		this.archive.addEventListener( 'submit', this.handleSubmit.bind( this ) );
		this.archive.addEventListener( 'change', this.handleChange.bind( this ) );
		this.archive.addEventListener( 'click', this.handleClick.bind( this ) );
		window.addEventListener( 'popstate', this.handlePopState.bind( this ) );

		// Seed the request from the URL so reloads restore the current page (the
		// server-seeded data-send can be stale behind a page cache).
		this.restoreFromUrl();
	}

	handleSubmit( event ) {
		if ( ! event.target.closest( ExamArchive.selectors.searchForm ) ) {
			return;
		}

		event.preventDefault();
		const dataSend = this.getDataSend();
		const search = this.archive.querySelector( ExamArchive.selectors.searchInput );
		dataSend.args.search = search ? search.value.trim() : '';
		dataSend.args.paged = 1;
		this.update( dataSend );
	}

	handleChange( event ) {
		if ( event.target.matches( ExamArchive.selectors.sort ) ) {
			const dataSend = this.getDataSend();
			dataSend.args.orderby = event.target.value;
			dataSend.args.paged = 1;
			this.update( dataSend );
			return;
		}

		if ( event.target.matches( ExamArchive.selectors.category ) ) {
			const dataSend = this.getDataSend();
			dataSend.args.exam_category = this.getCheckedValues( ExamArchive.selectors.category );
			dataSend.args.paged = 1;
			this.update( dataSend );
		}
	}

	handleClick( event ) {
		const pagination = event.target.closest( ExamArchive.selectors.pagination );
		if ( pagination ) {
			event.preventDefault();
			// Core (loadAJAX.js) binds a global click on `.page-numbers` that
			// also handles paging. Stop it so this archive's handler stays the
			// single source of truth for the URL + filter state.
			event.stopPropagation();

			const dataSend = this.getDataSend();
			const current = Math.max( 1, parseInt( dataSend.args.paged, 10 ) || 1 );
			let paged;
			if ( pagination.classList.contains( 'prev' ) ) {
				paged = current - 1;
			} else if ( pagination.classList.contains( 'next' ) ) {
				paged = current + 1;
			} else {
				paged = parseInt( pagination.textContent.trim(), 10 );
			}

			if ( ! paged || paged < 1 ) {
				return;
			}

			dataSend.args.paged = paged;
			this.update( dataSend, true );
			return;
		}

		if ( event.target.closest( ExamArchive.selectors.clear ) ) {
			event.preventDefault();
			const dataSend = this.getDataSend();
			dataSend.args.search = '';
			dataSend.args.orderby = 'newly_published';
			dataSend.args.exam_category = [];
			dataSend.args.paged = 1;
			this.syncControls( dataSend.args );
			this.update( dataSend );
		}
	}

	handlePopState() {
		this.restoreFromUrl();
	}

	restoreFromUrl() {
		const dataSend = this.getDataSend();
		dataSend.args = {
			...dataSend.args,
			...this.getUrlState(),
		};
		this.syncControls( dataSend.args );
		window.lpAJAXG.setDataSetCurrent( this.target, dataSend );
		this.load();
	}

	update( dataSend, scrollToResults = false ) {
		window.lpAJAXG.setDataSetCurrent( this.target, dataSend );
		this.updateUrl( dataSend.args );
		this.load( scrollToResults );
	}

	load( scrollToResults = false ) {
		const dataSend = this.getDataSend();
		const requestId = ++this.requestId;

		lpSetLoadingEl( this.results, 1 );
		this.target.setAttribute( 'aria-busy', 'true' );

		if ( scrollToResults ) {
			const top = this.results.getBoundingClientRect().top + window.scrollY - 100;
			window.scrollTo( { top, behavior: 'smooth' } );
		}

		window.lpAJAXG.fetchAJAX( dataSend, {
			success: ( response ) => {
				if ( requestId !== this.requestId ) {
					return;
				}

				const { status, message, data } = response || {};
				if ( 'success' !== status ) {
					this.renderError( message );
					return;
				}

				this.target.innerHTML = data?.content || '';
				if ( this.sidebar && data?.filters ) {
					this.sidebar.innerHTML = data.filters;
				}

				if ( data?.paged ) {
					dataSend.args.paged = data.paged;
					window.lpAJAXG.setDataSetCurrent( this.target, dataSend );
				}
			},
			error: () => {
				if ( requestId === this.requestId ) {
					this.renderError();
				}
			},
			completed: () => {
				if ( requestId !== this.requestId ) {
					return;
				}

				lpSetLoadingEl( this.results, 0 );
				this.target.removeAttribute( 'aria-busy' );
			},
		} );
	}

	getDataSend() {
		try {
			const dataSend = window.lpAJAXG.getDataSetCurrent( this.target );
			dataSend.args = dataSend.args || {};
			return dataSend;
		} catch ( error ) {
			return {
				action: 'load_exam_archive',
				args: this.getUrlState(),
			};
		}
	}

	getCheckedValues( selector ) {
		return Array.from( this.archive.querySelectorAll( `${ selector }:checked` ) ).map(
			( input ) => input.value
		);
	}

	getUrlState() {
		const params = new URL( window.location.href ).searchParams;
		const split = ( value ) => ( value ? value.split( ',' ).filter( Boolean ) : [] );
		const orderby = params.get( 'exam_orderby' ) || 'newly_published';

		return {
			search: params.get( 'exam_search' ) || '',
			orderby: [ 'newly_published', 'oldest', 'title_az', 'title_za' ].includes( orderby )
				? orderby
				: 'newly_published',
			exam_category: split( params.get( 'exam_category' ) ),
			paged: Math.max( 1, parseInt( params.get( 'paged' ), 10 ) || 1 ),
		};
	}

	updateUrl( args ) {
		const url = new URL( window.location.href );
		const setOrDelete = ( key, value ) => {
			if ( value ) {
				url.searchParams.set( key, value );
			} else {
				url.searchParams.delete( key );
			}
		};

		setOrDelete( 'exam_search', args.search || '' );
		setOrDelete(
			'exam_orderby',
			args.orderby && 'newly_published' !== args.orderby ? args.orderby : ''
		);
		setOrDelete( 'exam_category', ( args.exam_category || [] ).join( ',' ) );
		setOrDelete( 'paged', parseInt( args.paged, 10 ) > 1 ? args.paged : '' );
		window.history.pushState( {}, '', url );
	}

	syncControls( args ) {
		const search = this.archive.querySelector( ExamArchive.selectors.searchInput );
		const sort = this.archive.querySelector( ExamArchive.selectors.sort );
		if ( search ) {
			search.value = args.search || '';
		}
		if ( sort ) {
			sort.value = args.orderby || 'newly_published';
		}

		const selectedCategories = args.exam_category || [];
		this.archive.querySelectorAll( ExamArchive.selectors.category ).forEach( ( input ) => {
			input.checked = selectedCategories.includes( input.value );
		} );
	}

	renderError( message = '' ) {
		const fallback = this.archive.dataset.errorMessage || 'Unable to load exams. Please try again.';
		this.target.innerHTML = `
			<div class="lp-exam-archive__error" role="alert">
				${ this.escapeHtml( message || fallback ) }
			</div>
		`;
	}

	escapeHtml( value ) {
		const element = document.createElement( 'div' );
		element.textContent = value;
		return element.innerHTML;
	}
}
